<script setup>
import { ref, onMounted } from 'vue'

// eslint-disable-next-line no-unused-vars
defineProps({
  isPlaying: Boolean,
  toggleMusic: Function,
  timeline: Array
})

const emit = defineEmits(['next-chapter'])
const showNext = ref(false)

const observer = ref(null)
const timelineRef = ref(null)

// --- 新增：弹幕相关逻辑 ---
const danmakuList = ref([])

// 被点击的弹幕ID
const activeDanmakuId = ref(null)

// 点击弹幕处理函数
const handleDanmakuClick = (danmakuId) => {
  activeDanmakuId.value = danmakuId
  // 3秒后自动恢复到普通层级
  setTimeout(() => {
    if (activeDanmakuId.value === danmakuId) {
      activeDanmakuId.value = null
    }
  }, 3000)
}

// 预设的祝福语
const messages = [
  "遇见你是最幸运的事 ✨",
  "大耳狗觉得你超可爱 ☁️",
  "2026年也要一起去享受 ❤️",
  "每天都要开心哦 🎈",
  "感谢遇到你💫",
  "特别的爱给特别的你 💌",
  "想和你去吃好吃的 🍰",
  "星河滚烫，人间理想 🌟",
  "确认过眼神，是能一起干饭的人 🍚",
  "快乐这种事，我和你平分 🎉",
  "那天的风，和你一样温柔 💨",
  "下次还要一起，看同一场日落 🌇",
  "你笑的时候，连星星都亮了 ✨",
]

// 随机生成弹幕样式
const initDanmaku = () => {
  danmakuList.value = messages.map((text, i) => {
    return {
      id: i,
      text,
      // 随机高度：10% 到 70% 之间，避免挡住最底下的内容
      top: Math.floor(Math.random() * 60) + 10 + '%',
      // 随机速度：15s 到 25s 之间，慢一点比较浪漫
      duration: Math.floor(Math.random() * 10) + 15 + 's',
      // 随机延迟：0s 到 30s 之间错开，避免弹幕太集中
      delay: Math.floor(Math.random() * 30) + 's',
      // 随机背景色：更淡色系，不影响查看照片
      bg: ['rgba(255,255,255,0.6)', 'rgba(255,235,238,0.7)', 'rgba(224,247,250,0.7)'][Math.floor(Math.random() * 3)]
    }
  })
  // 初始化完成后显示下一章按钮
  showNext.value = true
}
// -----------------------

onMounted(() => {
  // 初始化弹幕
  initDanmaku()

  // 原有的观察器逻辑
  observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
          }
        })
      },
      { threshold: 0.1 }
  )

  const events = timelineRef.value?.querySelectorAll('.event')
  events?.forEach((event) => {
    observer.value.observe(event)
  })
})

const goNextChapter = () => {
  emit('next-chapter')
}
</script>

<template>
  <div class="timeline-section">

    <div class="danmaku-layer">
      <div
          v-for="item in danmakuList"
          :key="item.id"
          class="danmaku-item"
          :class="{ 'danmaku-active': activeDanmakuId === item.id }"
          :style="{
          top: item.top,
          backgroundColor: item.bg,
          animationDuration: item.duration,
          animationDelay: item.delay,
          zIndex: activeDanmakuId === item.id ? 1000 : 'auto'
        }"
        @click="handleDanmakuClick(item.id)"
      >
<!--        <span class="icon">☁️</span>-->
        {{ item.text }}
      </div>
    </div>
    <div class="timeline-header">
      <h1>我们的时光</h1>
    </div>

    <div ref="timelineRef" class="timeline">
      <div
          v-for="(event, index) in timeline"
          :key="index"
          class="event"
      >
        <div class="content-box">
          <span class="event-date">{{ event.date }}</span>
          <img v-if="event.img" :src="event.img" class="event-img" alt="事件图片" />
          <div class="event-text">{{ event.text }}</div>
        </div>
      </div>
    </div>

    <div v-if="showNext" class="next-chapter" @click="goNextChapter" role="button" tabindex="0">
      <span>下一章</span>
    </div>
  </div>
</template>

<style>
.timeline-section {
  padding: 24px 16px;
  min-height: 100vh;
  background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%);
  color: #333;
  overflow-x: hidden;
  position: relative; /* 确保弹幕层相对于这里或者视口定位 */
}

/* --- 新增：弹幕样式 --- */
.danmaku-layer {
  position: fixed; /* 固定在屏幕上，不随页面滚动而滚走，更有氛围感 */
  top: 0;
  left: 0;
  width: 100%;
  height: 80%;
  z-index: 999; /* 浮在最上层 */
  overflow: hidden;
}

.danmaku-item {
  position: absolute;
  left: 100%; /* 起始位置在屏幕右侧外 */
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  color: #666;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  gap: 5px;
  animation-name: slideLeft;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  cursor: pointer;
  transition: all 0.3s ease;
}

.danmaku-item:hover {
  transform: scale(1.05);
}

.danmaku-active {
  animation-play-state: paused; /* 点击时暂停动画 */
  box-shadow: 0 4px 12px rgba(255, 154, 158, 0.3);
  border: 2px solid rgba(255, 154, 158, 0.5);
  background-color: rgba(255, 255, 255, 0.95) !important;
}

/* 定义从右向左滚动的动画 */
/* 如果你坚持要“从左到右”，把 from 和 to 的 translateX 值互换即可 */
@keyframes slideLeft {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-100vw - 100%)); /* 移动到屏幕左侧外 */
  }
}
/* -------------------- */

.timeline-header { text-align: center; color: #fff; margin-bottom: 32px; position: relative; z-index: 2; }
.timeline-section .timeline-header h1 {
  font-size: 22px;
  margin-bottom: 6px;
  /* 优化字体栈：先适配中文，再兼容英文，最后兜底 */
  font-family: "Comic Sans MS", "Arial Rounded MT Bold", "Microsoft YaHei", "PingFang SC", "SimHei", Arial, sans-serif;
  letter-spacing: 1px;
  font-weight: bold;
  /* 可选：加!important临时测试优先级（确认生效后可去掉） */
  color: #fff !important;
}

.timeline {
  position: relative;
  max-width: 720px;
  margin: 0 auto;
  padding-bottom: 40px;
  z-index: 2; /* 确保内容在背景之上 */
}

.timeline::after {
  content: '';
  position: absolute;
  width: 4px;
  background: rgba(255,255,255,0.7);
  top: 0;
  bottom: 0;
  left: 28px;
  border-radius: 2px;
  z-index: -1;
}

.event {
  position: relative;
  margin-bottom: 28px;
  padding-left: 70px;
  opacity: 0;
  transform: translateY(24px);
  transition: all 0.7s ease-out;
}
.event.visible { opacity: 1; transform: translateY(0); }

.event::after {
  content: '❤';
  position: absolute;
  left: 14px;
  top: 12px;
  width: 32px;
  height: 32px;
  background: #fff;
  border: 3px solid #ff9a9e;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff9a9e;
  z-index: 100 !important;
  font-size: 20px;
  box-sizing: border-box;
}

.content-box {
  background: #fff;
  padding: 14px;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.08);
  overflow: hidden; /* 让图片完美贴合卡片的圆润边缘 */
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.content-box:hover {
  transform: scale(1.05); /* 轻微放大效果 */
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
}
.event-date {
  color: #e91e63;
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 8px;
  display: block;
  font-family: 'Segoe UI', 'Arial Rounded MT Bold', 'Comic Sans MS', Arial, sans-serif;
  letter-spacing: 0.5px;
}
.event-img {
  width: 100%;
  border-radius: 10px; /* 比外层卡片(12px)稍微小一点 */
  margin-bottom: 10px;
  background: #eee;
  object-fit: cover;
  max-height: 168px;
}
.event-text { font-size: 15px; color: #555; line-height: 1.6; white-space: pre-wrap; }

@media (max-width: 480px) {
  .event { padding-left: 60px; }
}

.next-chapter {
  margin-top: 32px;
  margin-bottom: 32px;
  display: inline-block;
  padding: 12px 20px;
  background: linear-gradient(90deg,#ffd1e6,#ff9a9e);
  color: #fff;
  border-radius: 24px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(255,140,160,0.18);
  font-weight: 700;
  z-index: 1001;
  user-select: none;
  font-size: 16px;
  font-family: "Comic Sans MS", "Arial Rounded MT Bold", "Microsoft YaHei", "PingFang SC", "SimHei", Arial, sans-serif;
  letter-spacing: 1px;
  transition: transform 0.2s ease;
}

.next-chapter:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(255,140,160,0.25);
}

.next-chapter:active {
  transform: scale(0.98) translateY(-2px);
}
</style>
